import {
    call,
    cancel,
    delay,
    fork,
    getContext,
    put,
    select,
    setContext,
    takeEvery,
} from 'redux-saga/effects';

import { findInObjectByPath } from '@elab/core/utils/findInObjectByPath';
import { excludedErrorTypesForGlobalMessages } from '@elab/core/utils/constants';
import { selectors as globalSelectors } from '@elab/core/store/global';
import isEmpty from '@elab/core/utils/isEmpty';

import validations from '../validations';
import { isBoolean } from '..';
import { actions as uiActions } from '../../store/ui';
import { actions as globalActions } from '../../store/global';
import { selectors as configSelectors } from '../../store/config';
import analytics from '../analytics';

import api from './api';
import {
    cleanPhoneNumber,
    dtoBuilder,
    getFieldPathFromDtoName,
    metadataConverter,
} from './helpers';
import getActions from './actions';
import getSelectors from './selectors';
import getTypes from './types';

export const submit = (
    doSubmit /*
A generator function where passed params are:
  - store.data
  - current step
  - context:= {actions, reducers, selectors}
*/,
    afterStepValidation /* Generator function where */,
    useGenericDtoBuilder,
    customDtoBuilder,
    beforeStepValidation,
    validateEmailAlreadyExist,
    beforeStepIncrement = null,
) =>
    function*({ payload } = {}) {
        const { noScroll, dialogInfo, type, formType, continueAsGuest, globalAuthErrorMessage } =
            payload || {};
        const ctx = yield getContext('ctx');
        const { selectors, actions } = ctx;
        if (yield select(selectors.isLoading)) return;

        yield put(actions.setLoading(true));
        yield put(actions.setStepValidationLoading(true));
        yield put(actions.resetGlobalMessage());

        const clearRegistrationPincode = yield select(configSelectors.getRegistrationClearPincode);

        if (clearRegistrationPincode && dialogInfo && dialogInfo.name) {
            if (dialogInfo.name === 'registrationSuccess') {
                yield put(uiActions.clearPincode(true));
            } else if (dialogInfo.name === 'pincode-form-popup') {
                yield put(uiActions.clearPincode(false));
            }
        }

        const removeMessagesOnSubmit = yield select(configSelectors.removeDynaFormMessagesOnSubmit);
        if (removeMessagesOnSubmit) {
            yield put(globalActions.removeAllMessages());
        }
        const metadata = yield select(selectors.getMetadata);
        const step = yield select(selectors.getStep);
        let data = yield select(selectors.getData);

        const skipValidationRules = yield select(configSelectors.getSkipValidationRules);

        try {
            const currMetadataFields = (metadata[step - 1] || {}).fields || {};
            let valid = true;
            let firstFieldInError = null;

            for (let name of Object.keys(currMetadataFields)) {
                const fieldData = data[name];
                let skipValidation = yield call(
                    shouldSkipValidation,
                    skipValidationRules,
                    ctx,
                    name,
                );

                if (continueAsGuest && fieldData.name == 'password') {
                    skipValidation = true;
                }

                const fieldValid = yield call(
                    doValidation,
                    { name, value: fieldData.value, skipValidation },
                    fieldData.constraints || [],
                );
                valid = valid && !!fieldValid;

                if (fieldData.fields) {
                    const valuesValid = yield call(validateMultivalField, fieldData);
                    valid = valid && valuesValid;
                }

                if (!valid && !firstFieldInError) {
                    firstFieldInError = name;
                }
            }

            if (!valid && firstFieldInError) {
                const shouldCheckFailedValidation = yield select(
                    configSelectors.checkFailedFormValidation,
                );
                if (shouldCheckFailedValidation) {
                    yield put(actions.setValidationFailed());
                }
                if (noScroll) {
                    return;
                }
                yield put(
                    actions.setScrollToField({
                        name: firstFieldInError,
                        value: true,
                    }),
                );
                return;
            }

            if (beforeStepValidation) {
                const canContinue = yield call(beforeStepValidation, ctx);
                if (!canContinue) return;
            }

            let submitUrl = yield select(selectors.getSubmitUrl);

            data = yield select(selectors.getData);
            const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);

            let dto = yield call(customDtoBuilder || dtoBuilder, data, {}, selectors);
            if (shouldCleanPhoneNumber) {
                dto = cleanPhoneNumber(dto);
            }
            const isDlrRegistered = dto && dto.isDlrRegistered;
            const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
            if (isDlrRegistered && deliveryCountry) {
                dto.defaultAddress.country.isocode = deliveryCountry;
            }
            let response;

            if (metadata && metadata[step - 1] && metadata[step - 1].submit) {
                response = yield call(api.doStepValidation, metadata[step - 1].submit, dto);
            }

            if (!response || response.ok) {
                if (
                    dto &&
                    dto.loyaltyInformation &&
                    dto.loyaltyInformation.cardNumber &&
                    step === 2
                ) {
                    analytics.handleLoyaltyCardRegistrationEvent(dto.loyaltyInformation.cardNumber);
                }
                if (afterStepValidation) {
                    const canContinue = yield call(afterStepValidation, ctx);
                    if (!canContinue) return;
                }

                data = yield select(selectors.getData);
                dto = yield call(customDtoBuilder || dtoBuilder, data, {}, selectors);
                if (shouldCleanPhoneNumber) {
                    dto = cleanPhoneNumber(dto);
                }

                if (step === metadata.length || formType === 'guestToLoginForm') {
                    let _data = useGenericDtoBuilder ? dto : data;
                    if (formType === 'guestToLoginForm') {
                        if (!continueAsGuest && step === 2) {
                            _data = yield call(customGuestLoginDtoBuilder, data);
                        }
                        submitUrl = metadata[step - 1].submit;
                    }
                    yield call(
                        doSubmit,
                        _data,
                        ctx,
                        submitUrl,
                        dialogInfo,
                        formType,
                        step,
                        continueAsGuest,
                        globalAuthErrorMessage,
                    );
                    yield put(actions.formSubmitted(_data));
                    yield call(updateSuccessGlobalMessage, noScroll);
                } else {
                    if (beforeStepIncrement) {
                        const canContinue = yield call(beforeStepIncrement, ctx);
                        if (!canContinue) return;
                    } else {
                        yield put(uiActions.scrollToCenter());
                    }
                    yield put(actions.setStep(step + 1)); // to do call submit from step metadata
                }
            } else {
                const { errors = [] } = yield response.json();
                const shouldCheckEmailAlreadyExist = yield select(
                    configSelectors.shouldCheckEmailAlreadyExist,
                );
                if (validateEmailAlreadyExist && shouldCheckEmailAlreadyExist) {
                    const canContinue = yield call(
                        validateEmailAlreadyExist,
                        errors,
                        data.uid.value,
                        ctx.storeName,
                    );
                    if (!canContinue) return;
                }

                if (dialogInfo && dialogInfo.name)
                    yield put(uiActions.closeDialogByName(dialogInfo.name));
                if (
                    errors &&
                    errors[0] &&
                    errors[0].id === 'Dynamic_UserSignUpWsDTO_customer_status_db_validation' &&
                    formType === 'guestToLoginForm'
                ) {
                    yield put(actions.setStep(step + 1));
                } else {
                    yield call(checkStepChanging, errors, metadata, step, actions);
                    yield call(showErrors, errors, data);
                }
            }
        } catch (e) {
            const errors = e.json ? e.json.errors : [];
            if (dialogInfo && dialogInfo.name)
                yield put(uiActions.closeDialogByName(dialogInfo.name));
            yield call(checkStepChanging, errors, metadata, step, actions);
            yield call(showErrors, errors, data, noScroll);
        } finally {
            yield put(actions.setLoading(false));
            yield put(actions.setStepValidationLoading(false));
        }
    };

export function* customGuestLoginDtoBuilder(data) {
    const dto = {
        username: data.uid.value,
        password: data.password.value,
        rememberMe: data.keepSignedIn.value,
    };
    return dto;
}

export function* checkStepChanging(errors, metadata, step, actions) {
    const switchToFirstNotValidStep = yield select(configSelectors.getSwitchToFirstNotValidStep);
    if (switchToFirstNotValidStep) {
        const firstNotValidatedStep = getFirstNotValidatedStep(errors, metadata);

        const isCurrentStep = firstNotValidatedStep === step;

        if (firstNotValidatedStep && !isCurrentStep) {
            yield put(actions.setStep(firstNotValidatedStep));
        }
    }
    yield delay(0);
}

function getFirstNotValidatedStep(errors, metadata) {
    const index = metadata.findIndex(step => {
        const hasError = errors.some(error => findErrorField(error.subject, step.fields));
        if (hasError) return true;
    });
    const step = index + 1 || null;

    return step;
}

function findErrorField(fieldName = '', fields = {}) {
    return Object.values(fields).some(inputField =>
        inputField.fields
            ? findErrorField(fieldName, inputField.fields)
            : getFieldPathFromDtoName(fieldName, fields),
    );
}

export function* shouldSkipValidation(skipValidationRules, ctx, name) {
    const currentRule = skipValidationRules[ctx.storeName] || {};

    if (isEmpty(currentRule)) {
        return false;
    }

    if (currentRule.fieldsToSkip.includes(name)) {
        const conditionFieldName = Object.keys(currentRule.condition)[0];
        const conditionFieldValue = yield select(
            ctx.selectors.getDataFieldValue,
            conditionFieldName,
        );
        if (conditionFieldValue === currentRule.condition[conditionFieldName]) {
            yield put(ctx.actions.setDataFieldValue(name, null, true));
            return true;
        }
    }

    return false;
}

export function* showErrors(errors = [], data, noScroll) {
    const { actions, selectors, storeName } = yield getContext('ctx');
    const msgSeparator = '. ';
    const endCharacter = '.';
    const uniqueErrors =
        errors
            .filter(({ type }) => excludedErrorTypesForGlobalMessages.some(error => error === type))
            .reduce((acc, cur) => {
                acc[cur.subject] = acc[cur.subject] || [];
                if (acc[cur.subject].indexOf(cur.message) === -1) {
                    acc[cur.subject].push(cur.message);
                }
                return acc;
            }, {}) || {};

    let firstFieldInError = null;
    for (let key in uniqueErrors) {
        const fieldPath = getFieldPathFromDtoName(key, data);
        if (
            fieldPath.split('.')[0] == 'loyaltyOptionsForRegistrationForm' &&
            (storeName == 'registrationForm' || 'checkout-registration')
        ) {
            yield put(actions.showPincode(false));
        }
        const value = uniqueErrors[key];
        if (!fieldPath) continue;

        let message = (findInObjectByPath(data, fieldPath.split('.')) || {}).singleErrorMode
            ? value[0]
            : value.join(msgSeparator);
        if (message.charAt(message.length - 1) !== endCharacter) {
            message += endCharacter;
        }

        yield put(actions.setDataFieldError(fieldPath, message || ''));
        firstFieldInError = firstFieldInError || fieldPath;
        if (storeName === 'enrollmentPinCodeForm')
            yield put(actions.parent.setDataFieldError(fieldPath, message || ''));
    }

    if (Object.keys(uniqueErrors).length) {
        if (!noScroll) {
            yield put(
                actions.setScrollToField({
                    name: firstFieldInError,
                    value: true,
                }),
            );
        }
    } else {
        // GenericError this is special type of error for blacklist validation
        const genericError = errors.find(({ type }) => type === 'GenericError');
        const bruteForceAttackError = errors.find(({ type }) => type === 'BruteForceAttackError');

        if (genericError) {
            yield put(actions.setGlobalError('', 'GenericError'));
        }
        if (bruteForceAttackError) {
            yield put(
                actions.setGlobalError(bruteForceAttackError.message, 'BruteForceAttackError'),
            );
        } else {
            let { other } = yield select(selectors.getErrorsText);
            if (!other) {
                // if the Other Error message is not available then get it from the form element context.
                const formContext = yield select(selectors.getFormElement);
                other = formContext ? formContext.otherErrorText : other;
            }

            yield put(actions.setGlobalError(other || (errors[0] && errors[0].message), 'other'));
        }

        if (!noScroll) {
            yield put(uiActions.scrollToCenter());
        }
    }
}

export function* updateSuccessGlobalMessage(noScroll) {
    const { actions, selectors, storeName } = yield getContext('ctx');

    const formGlobalMessage = yield select(selectors.getGlobalMessage);
    const formElement = yield select(selectors.getFormElement);
    const shouldScrollToFormCenterBySuccess = yield select(
        configSelectors.getShouldScrollToFormCenterBySuccess,
        storeName,
    );

    if (!formGlobalMessage.error) {
        yield put(actions.setGlobalSuccess());
    }
    if (!noScroll) {
        const scrollOptions =
            shouldScrollToFormCenterBySuccess && formElement ? { element: formElement } : {};

        yield put(uiActions.scrollToCenter(scrollOptions));
    }
}

export function* validateMultivalField(fieldData) {
    let valid = true;
    const valueLen = fieldData.value && fieldData.value.length;

    for (let index = 0; index < valueLen; index++) {
        const fieldDataObj = fieldData.value[index];
        const fieldDataObjFields = Object.values(fieldDataObj);

        for (let fieldIdx = 0; fieldIdx < fieldDataObjFields.length; fieldIdx++) {
            const fieldDataVal = fieldDataObjFields[fieldIdx];
            const fieldValid = yield call(
                doValidation,
                {
                    name: `${fieldData.name}.value.${index}.${fieldDataVal.name}`,
                    value: fieldDataVal.value,
                },
                fieldDataVal.constraints || [],
            );

            if (fieldDataVal.fields) {
                const valuesValid = yield call(validateMultivalField, fieldDataVal);
                valid = valid && valuesValid;
            }
            valid = valid && !!fieldValid;
        }
    }
    return valid;
}

export function* doValidation({ name, value, skipValidation }, constraints = []) {
    const { selectors, actions, storeName } = yield getContext('ctx');
    const data = yield select(selectors.getData);

    const dataField = yield select(selectors.getDataField, name);
    let hasErrors = false;

    if (dataField && constraints) {
        // Have to use plain 'for' loop avoiding new function creation to be able to use 'yield' inside
        for (let idx = 0; idx < constraints.length; idx++) {
            const validator = constraints[idx];
            if (!validator) {
                continue;
            }

            let { fieldCompare } = validator.parameters || {};
            let compareValue;
            if (fieldCompare) {
                if (validator.type === 'pattern') {
                    const fieldCompareName = getFieldPathFromDtoName(fieldCompare, data);
                    fieldCompare = fieldCompareName;
                    compareValue = yield select(selectors.getDataFieldValue, fieldCompareName);
                }

                const isFieldCompareLinked = yield select(
                    selectors.getDataFieldLink,
                    fieldCompare,
                    name,
                );
                if (!isFieldCompareLinked) {
                    yield put(actions.addDataFieldLink(fieldCompare, name));
                }
            }

            if (!skipValidation) {
                const linkedFields = yield select(selectors.getDataFieldLinks, name);
                for (const { link: linkedField } of linkedFields) {
                    const linkedValue = yield select(selectors.getDataFieldValue, linkedField);
                    const linkedFieldConstraints = yield select(
                        selectors.getDataFieldConstraints,
                        linkedField,
                    );

                    yield call(
                        doValidation,
                        {
                            name: linkedField,
                            value: linkedValue,
                            skipValidation: false,
                        },
                        linkedFieldConstraints,
                    );
                }
            }

            const validationFunction = validations[validator.type];
            const trimValue = yield select(configSelectors.trimValue);

            const removeMobileNumberFormat = yield select(configSelectors.removeMobileNumberFormat);

            let preservePhoneNumberPattern = yield select(selectors.getPreservePhoneNumberPattern);

            preservePhoneNumberPattern =
                storeName == 'personal-information' ? preservePhoneNumberPattern : false;

            if (
                name === 'mobileNumber' &&
                removeMobileNumberFormat &&
                !preservePhoneNumberPattern
            ) {
                value = value && value.replace(/\D/g, '');
            }
            const isValid =
                !validationFunction ||
                validationFunction(value, data, validator.parameters, compareValue, trimValue);

            if (!!validator.valid !== isValid) {
                yield put(
                    actions.setIsConstraintValid({
                        name,
                        constraintIdx: idx,
                        isValid,
                    }),
                );
            }

            const errorMessage = validator.errorMessage;

            if (!isValid && !skipValidation && value !== undefined && !hasErrors) {
                if (dataField.error !== errorMessage) {
                    yield put(actions.setDataFieldError(name, errorMessage));
                }

                hasErrors = true;
            }
        }

        if (!hasErrors && dataField.error && !skipValidation) {
            yield put(actions.setDataFieldError(name, ''));
        }
    }
    return !hasErrors;
}

export function mapDeferredConstraints(processedField = {}) {
    const { touched, constraints = [] } = processedField;
    // constraint order in array is used as it's id; set constraint value to 'false' to don't break the order
    return touched
        ? constraints
        : constraints.map(constraint => !constraint.deferred && constraint);
}

export function* doFieldValidation(payload) {
    const { selectors, actions } = yield getContext('ctx');
    const processedField = yield select(selectors.getDataField, payload.name);

    if (processedField && processedField.constraints) {
        const constraints = mapDeferredConstraints(processedField);
        yield call(doValidation, payload, constraints);
    }

    if (!(yield select(selectors.isStepValidationLoading))) {
        yield put(actions.dataFieldValidationDone(payload));
    }
}

export function* setDataField({ payload }) {
    const { name } = payload;
    const tasks = yield getContext('validationTasks');

    if (tasks[name] && !tasks[name].isCancelled()) {
        yield cancel(tasks[name]);
    }

    tasks[name] = yield fork(function*() {
        yield delay(300);
        yield call(doFieldValidation, payload);
    });
}

export function* blurFieldWrapper(action) {
    const blurTasks = yield getContext('blurTasks');
    const name = action.payload;

    if (blurTasks[name] && !blurTasks[name].isCancelled()) {
        yield cancel(blurTasks[name]);
    }
    blurTasks[name] = yield fork(function*() {
        yield delay(100);
        yield call(blurField, action);
    });
}

export function* blurField({ payload: name }) {
    const { actions, selectors } = yield getContext('ctx');
    const processedField = yield select(selectors.getDataField, name);
    const constraints = mapDeferredConstraints(processedField);

    yield call(
        doValidation,
        {
            name, // subforms' elements names are the path and not the name of field
            value: processedField && processedField.value,
            skipValidation: false,
        },
        constraints,
    );

    if (!(yield select(selectors.isStepValidationLoading))) {
        const addDeferedValidated = yield select(configSelectors.getAddDeferedValidated);
        yield put(
            actions.dataFieldValidationDone({
                name: addDeferedValidated ? name : processedField && processedField.name,
                value: processedField && processedField.value,
                skipValidation: false,
                deferredValidated: addDeferedValidated,
            }),
        );
    }
}

export function* resetData() {
    const { actions, selectors } = yield getContext('ctx');
    const store = yield select(selectors.getStore);
    const data =
        store.metadata && store.metadata.length ? store.metadata.reduce(metadataConverter, {}) : {};
    yield put(actions.setInitialData({ ...store, data }));
}

export const isRequired = metadata =>
    (metadata.constraints || []).some(validator => validator.type === 'required');

export function* initialize() {
    const ctx = yield getContext('ctx');
    const { actions, metadataSource, storeName } = ctx;
    let _metadata;

    try {
        if (metadataSource instanceof Function /* meant to be an generator func */) {
            _metadata = yield call(metadataSource, ctx);
        } else if (metadataSource instanceof String || typeof metadataSource === 'string') {
            _metadata = yield call(api.loadMetadata, metadataSource);
        } else {
            _metadata = metadataSource;
        }
    } catch (e) {
        _metadata = {
            metaError: e.error,
            steps: [],
        };
    }

    const metadata = _metadata.steps.map(step => ({
        ...step,
        fields: step.fields.reduce(
            (acc, field) => ({
                ...acc,
                [field.name]: field,
            }),
            {},
        ),
        validator: step.validator,
    }));

    const data = metadata.reduce(metadataConverter, {});

    const isDefaultCheckoutCountryCheckEnabled = yield select(
        configSelectors.isDefaultCheckoutCountryCheckEnabled,
    );

    if (isDefaultCheckoutCountryCheckEnabled && storeName === 'shippingAddress') {
        const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
        const countryDefaultVal = metadata[0].fields.country
            ? metadata[0].fields.country.defaultValue
            : '';

        if (deliveryCountry && countryDefaultVal && deliveryCountry !== countryDefaultVal) {
            metadata[0].fields.country.defaultValue = deliveryCountry;
        }
    }

    yield put(actions.setInitialData({ data, metadata }));
    yield put(actions.setSubmitUrl(_metadata.submit));
    yield put(actions.setSubmitMethod(_metadata.submitMethod || ''));
    yield put(actions.setInitialized());
}

// this saga is congruent to resetData()
export function* addFieldItem({ payload: { name: fieldName, value: fieldValueObj = {} } }) {
    const { actions, selectors } = yield getContext('ctx');
    const field = yield select(selectors.getDataField, fieldName);
    if (!field) return;

    let newValue = [
        ...((field && field.value) || []),
        field.fields.reduce((acc, fieldMetadata) => {
            const getDefaultValue = () =>
                isBoolean(fieldMetadata.defaultValue)
                    ? JSON.parse(fieldMetadata.defaultValue)
                    : fieldMetadata.defaultValue;

            const required = isRequired(fieldMetadata);
            const value = fieldValueObj[fieldMetadata.name] || getDefaultValue() || null;

            return {
                [fieldMetadata.name]: {
                    ...fieldMetadata,
                    value,
                    required,
                    error: '',
                },
                ...acc,
            };
        }, {}),
    ];

    yield put(actions.setDataFieldValue(fieldName, newValue));
}

/*
createSagaFor
storeName:= first level state name
metadataSource:= string url or metadata object
doSubmit:= Generator function where
Passed params:
  - store.data
  - context:= {actions, reducers, selectors}
*/
const createSagaFor = (
    store,
    metadataSource,
    doSubmit,
    afterStepValidation,
    useGenericDtoBuilder,
    customDtoBuilder,
    beforeStepValidation,
    customInitialize = initialize,
    validateEmailAlreadyExist,
    beforeStepIncrement = null,
) => {
    const types = store.types || getTypes(store);
    const selectors = store.selectors || getSelectors(store);
    const actions = store.actions || getActions(store);
    const storeName = store.storeName;
    const ctx = { storeName, types, metadataSource, selectors, actions };
    let validationTasks = {};
    let blurTasks = {};
    if (!customInitialize) {
        customInitialize = initialize;
    }
    return [
        setContext({ ctx, validationTasks, blurTasks }),
        takeEvery(types.INITIALIZE, customInitialize),
        takeEvery(types.SET_DATA_FIELD_VALUE, setDataField),
        takeEvery(types.ADD_FIELD_ITEM, addFieldItem),
        takeEvery(
            types.SUBMIT,
            submit(
                doSubmit,
                afterStepValidation,
                useGenericDtoBuilder,
                customDtoBuilder,
                beforeStepValidation,
                validateEmailAlreadyExist,
                beforeStepIncrement,
            ),
        ),
        takeEvery(types.RESET_FORM_DATA, resetData),
        takeEvery(types.BLUR_FIELD, blurFieldWrapper),
    ];
};

export default createSagaFor;
